import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { booksApi, charactersApi } from '../lib/api';
import { getDeletedFiles, restoreFromTrash, permanentlyDelete, emptyTrash } from '../lib/imageStorage';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
    ChevronLeft, Trash2, RotateCcw, AlertTriangle, Image, Clock, 
    Folder, BookOpen, Archive 
} from 'lucide-react';

const DeletedFiles = ({ onBack }) => {
    const { user } = useAuth();
    const [books, setBooks] = useState([]);
    const [selectedBook, setSelectedBook] = useState(null);
    const [characters, setCharacters] = useState([]);
    const [deletedFiles, setDeletedFiles] = useState([]);
    const [loading, setLoading] = useState(true);

    // Load user's books
    useEffect(() => {
        const loadBooks = async () => {
            try {
                const res = await booksApi.list();
                setBooks(res.data || []);
                if (res.data && res.data.length > 0) {
                    setSelectedBook(res.data[0]);
                }
            } catch (error) {
                console.error('Failed to load books');
            } finally {
                setLoading(false);
            }
        };
        loadBooks();
    }, []);

    // Load characters and deleted files when book changes
    useEffect(() => {
        const loadData = async () => {
            if (!selectedBook) return;
            
            try {
                const res = await charactersApi.list(selectedBook.id);
                setCharacters(res.data || []);
            } catch (error) {
                console.error('Failed to load characters');
            }
            
            const files = getDeletedFiles(selectedBook.id);
            setDeletedFiles(files);
        };
        loadData();
    }, [selectedBook]);

    const handleRestore = (imageId) => {
        if (!selectedBook) return;
        restoreFromTrash(selectedBook.id, imageId, characters);
        const files = getDeletedFiles(selectedBook.id);
        setDeletedFiles(files);
        toast.success('Image restored to character gallery');
    };

    const handlePermanentDelete = (imageId) => {
        if (!selectedBook) return;
        permanentlyDelete(selectedBook.id, imageId);
        const files = getDeletedFiles(selectedBook.id);
        setDeletedFiles(files);
        toast.success('Image permanently deleted');
    };

    const handleEmptyTrash = () => {
        if (!selectedBook) return;
        emptyTrash(selectedBook.id);
        setDeletedFiles([]);
        toast.success('Trash emptied');
    };

    const getDaysRemaining = (expiresAt) => {
        const now = new Date();
        const expires = new Date(expiresAt);
        const diff = Math.ceil((expires - now) / (1000 * 60 * 60 * 24));
        return Math.max(0, diff);
    };

    const getCharacterName = (characterId) => {
        const char = characters.find(c => c.id === characterId);
        return char?.name || 'Unknown Character';
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="text-center">
                    <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-muted-foreground">Loading...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="h-14 border-b border-border flex items-center px-4 gap-4 sticky top-0 bg-background z-40">
                <Button variant="ghost" size="sm" onClick={onBack}>
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Back
                </Button>
                <div className="flex items-center gap-2">
                    <Archive className="h-5 w-5 text-accent" />
                    <h1 className="font-serif text-lg font-semibold">Deleted Files</h1>
                </div>
            </header>

            <main className="max-w-5xl mx-auto p-8">
                {/* Info Banner */}
                <div className="mb-6 p-4 bg-muted/50 rounded-lg flex items-start gap-3">
                    <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                        <p className="font-medium">Recoverable for 10 days</p>
                        <p className="text-sm text-muted-foreground">
                            Deleted images are kept here before being permanently removed. 
                            Restore them to put them back in the character's gallery.
                        </p>
                    </div>
                </div>

                {/* Book Selector */}
                {books.length > 1 && (
                    <div className="mb-6">
                        <label className="text-sm font-medium mb-2 block">Select Book</label>
                        <div className="flex flex-wrap gap-2">
                            {books.map(book => (
                                <Button
                                    key={book.id}
                                    variant={selectedBook?.id === book.id ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setSelectedBook(book)}
                                >
                                    <BookOpen className="h-4 w-4 mr-2" />
                                    {book.title}
                                </Button>
                            ))}
                        </div>
                    </div>
                )}

                {/* No Books */}
                {books.length === 0 && (
                    <div className="text-center py-16">
                        <BookOpen className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
                        <h2 className="font-serif text-xl font-semibold mb-2">No books yet</h2>
                        <p className="text-muted-foreground mb-4">
                            Create a book first to manage deleted files.
                        </p>
                        <Button onClick={onBack}>Go to Dashboard</Button>
                    </div>
                )}

                {/* Deleted Files */}
                {selectedBook && (
                    <>
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="font-serif text-xl font-semibold">
                                {selectedBook.title} - Trash ({deletedFiles.length})
                            </h2>
                            {deletedFiles.length > 0 && (
                                <Button 
                                    variant="destructive" 
                                    size="sm"
                                    onClick={handleEmptyTrash}
                                >
                                    <AlertTriangle className="h-4 w-4 mr-2" />
                                    Empty Trash
                                </Button>
                            )}
                        </div>

                        {deletedFiles.length === 0 ? (
                            <div className="text-center py-16 card-default">
                                <Trash2 className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
                                <h3 className="font-serif text-lg font-semibold mb-2">Trash is empty</h3>
                                <p className="text-sm text-muted-foreground">
                                    No deleted files for this book.
                                </p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                {deletedFiles.map((file) => (
                                    <div key={file.id} className="card-default p-3">
                                        <div className="aspect-square rounded-lg overflow-hidden bg-muted mb-3">
                                            {file.url ? (
                                                <img 
                                                    src={file.url} 
                                                    alt={file.title}
                                                    className="w-full h-full object-cover opacity-70"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <Image className="h-12 w-12 text-muted-foreground/30" />
                                                </div>
                                            )}
                                        </div>
                                        <p className="font-medium text-sm truncate">{file.title}</p>
                                        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                                            <Folder className="h-3 w-3" />
                                            {getCharacterName(file.characterId)}
                                        </p>
                                        <p className="text-xs text-destructive flex items-center gap-1 mt-1">
                                            <Clock className="h-3 w-3" />
                                            {getDaysRemaining(file.expiresAt)} days until permanent deletion
                                        </p>
                                        <div className="flex gap-2 mt-3">
                                            <Button 
                                                size="sm" 
                                                variant="outline" 
                                                className="flex-1"
                                                onClick={() => handleRestore(file.id)}
                                            >
                                                <RotateCcw className="h-3 w-3 mr-1" />
                                                Restore
                                            </Button>
                                            <Button 
                                                size="sm" 
                                                variant="ghost"
                                                className="text-destructive"
                                                onClick={() => handlePermanentDelete(file.id)}
                                            >
                                                <Trash2 className="h-3 w-3" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </main>
        </div>
    );
};

export default DeletedFiles;
